import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Globe } from 'lucide-react';
import { apiService } from '../../services/api';

const toPercentInput = (decimal) => {
  const value = Number(decimal);
  if (!Number.isFinite(value)) return '';
  return String(parseFloat((value * 100).toFixed(6)));
};

const fromPercentInput = (value) => {
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed / 100;
};

const WebPortalAllocationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allocatorEmail, setAllocatorEmail] = useState('');
  const [serviceSharePercent, setServiceSharePercent] = useState('66.67');
  const [branches, setBranches] = useState([]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getWebPortalAllocationSettings();
      setAllocatorEmail(data.allocatorEmail || '');
      setServiceSharePercent(toPercentInput(data.rules?.serviceShare));
      setBranches((data.rules?.branches || []).map((branch) => ({
        name: branch.name || '',
        salesPercent: toPercentInput(branch.salesPercent),
        salesGl: branch.salesGl || '',
        servicePercent: toPercentInput(branch.servicePercent),
        serviceGl: branch.serviceGl || ''
      })));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const updateBranch = (index, field, value) => {
    setBranches((current) =>
      current.map((branch, branchIndex) =>
        branchIndex === index ? { ...branch, [field]: value } : branch
      )
    );
  };

  const salesPercentTotal = branches.reduce((sum, branch) => sum + (parseFloat(branch.salesPercent) || 0), 0);
  const servicePercentTotal = branches.reduce((sum, branch) => sum + (parseFloat(branch.servicePercent) || 0), 0);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiService.updateWebPortalAllocationSettings({
        allocatorEmail,
        rules: {
          serviceShare: fromPercentInput(serviceSharePercent),
          branches: branches.map((branch) => ({
            name: branch.name.trim(),
            salesPercent: fromPercentInput(branch.salesPercent),
            salesGl: branch.salesGl.trim(),
            servicePercent: fromPercentInput(branch.servicePercent),
            serviceGl: branch.serviceGl.trim()
          }))
        }
      });
      setSuccess('Web Portal allocation settings saved');
      await loadSettings();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Globe size={28} style={{ marginRight: 12 }} />
        <Typography variant="h5">Web Portal Allocation</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tagged Web Portal charges on the designated allocator&apos;s expense reports are summed,
        then split using this table. Service receives the share below; Sales receives the remainder.
        The last branch in each division absorbs leftover pennies so variance is $0.00.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Allocator</Typography>
        <TextField
          fullWidth
          label="Allocator email"
          value={allocatorEmail}
          onChange={(e) => setAllocatorEmail(e.target.value)}
          helperText="Only this user's expense reports show the Web Portal checkbox"
          sx={{ maxWidth: 420, mt: 1 }}
        />
        <TextField
          label="Service division share (%)"
          value={serviceSharePercent}
          onChange={(e) => setServiceSharePercent(e.target.value)}
          helperText={`Sales share will be ${(100 - (parseFloat(serviceSharePercent) || 0)).toFixed(2)}%`}
          sx={{ maxWidth: 240, mt: 3, display: 'block' }}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Branch splits and GL codes</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Branch</TableCell>
              <TableCell>Sales %</TableCell>
              <TableCell>Sales GL</TableCell>
              <TableCell>Service %</TableCell>
              <TableCell>Service GL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {branches.map((branch, index) => (
              <TableRow key={`${branch.name}-${index}`}>
                <TableCell>
                  <TextField
                    size="small"
                    value={branch.name}
                    onChange={(e) => updateBranch(index, 'name', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={branch.salesPercent}
                    onChange={(e) => updateBranch(index, 'salesPercent', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={branch.salesGl}
                    onChange={(e) => updateBranch(index, 'salesGl', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={branch.servicePercent}
                    onChange={(e) => updateBranch(index, 'servicePercent', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={branch.serviceGl}
                    onChange={(e) => updateBranch(index, 'serviceGl', e.target.value)}
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>Totals</TableCell>
              <TableCell>{salesPercentTotal.toFixed(2)}%</TableCell>
              <TableCell />
              <TableCell>{servicePercentTotal.toFixed(2)}%</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save settings'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default WebPortalAllocationSettings;
